import type { PPProviderBehavior } from '../types';
import { PaymentType } from '../../typegen/payment-type';
import EMVDevice from '../../devices/emv-device/EMVDevice';
import { DeviceStatusFlags } from '../../devices/MonitoredDevice';
import type { StatusDetails } from '../../devices/MonitoredDevice';

const EMV_FIELD_LABELS: Record<string, string> = {
	Started: 'Started',
	Registered: 'Registered',
	Connected: 'Connected',
	Encryption: 'Encryption Active',
	CardInReader: 'Card In Reader',
	InPayment: 'In Payment',
	OfflineRecovery: 'Offline Recovery',
	'~OfflineTxnsAvail': 'Offline Txns Available',
	OfflineTxnsAvail: 'Offline Txns Available',
	AppLoaderStatus: 'App Loader Status',
};

// VP5300 prompts that do not apply to the kiosk (there is no signature capture).
// Compared lower-case and exact, not as a substring.
const IGNORED_DEVICE_MESSAGES = ['please sign receipt'];

function mapEmvDeviceState(deviceState: Record<string, unknown>): StatusDetails {
	const details: StatusDetails = {};
	for (const [key, val] of Object.entries(deviceState)) {
		const label = EMV_FIELD_LABELS[key] ?? key;
		details[label] = val === 0 || val === 1 ? (val === 1 ? 'Yes' : 'No') : String(val);
	}
	return details;
}

export const fiservBehavior: PPProviderBehavior = {
	label: 'Fiserv',
	cardDeviceDisplayName: 'VP5300',
	requiresPrepareTransaction: false,
	requiresPayorInformation: false,
	cardPrepareAutoStart: false,

	/**
	 * Checks whether the VP5300 EMV card reader is connected and in a ready state.
	 * Requires both GetState() === 1 (Ready) and GetStatusEx() reporting Connected.
	 * Returns false if the device is unavailable or throws.
	 */
	isCardDeviceAvailable: () => {
		try {
			if (window.KioEMVDevice?.GetState() !== 1) return false;
			const status = fiservBehavior.getEMVDeviceStatus?.();
			if (!status) return false;
			return status.details['Connected'] === 'Yes';
		} catch {
			return false;
		}
	},

	/**
	 * Builds the prepare-transaction request payload for card payments.
	 * Maps each cart item to an account-to-pay entry and attaches
	 * the kiosk location, total amount, and payor information.
	 *
	 * Called by payment-card-token before initiating the EMV sale.
	 */
	buildPrepareCardRequest(ctx) {
		return {
			accountsToPay: ctx.cartItems.map((item) => ({
				accountNumber: item.item.itemNumber,
				amount: item.amountToPay,
			})),
			paymentType: 'CREDIT',
			location: ctx.kioskLocation,
			totalAmount: ctx.cartTotalAmount,
			payorInfo: ctx.payorInformation,
		};
	},

	/**
	 * Resolves the card fee for the service-fee confirmation screen.
	 * Sums the per-item fee across all cart items by delegating to
	 * resolvePaymentFee, then returns formatted strings for the UI.
	 *
	 * Called by payment-card-token during card payment initialization.
	 *
	 * @returns ResolvedFee with fee and total as formatted strings (e.g. "0.60", "20.60")
	 */
	resolveCardFee(ctx) {
		const fee = ctx.cartItems.reduce((sum, item) => sum + (this.resolvePaymentFee?.(item, PaymentType.Card) ?? 0), 0);
		const total = ctx.cartTotalAmount + fee;
		return { fee: fee.toFixed(2), total: total.toFixed(2) };
	},

	/**
	 * Single source of truth for fee calculation across all payment types.
	 * Looks up the payment method on the item that matches the given paymentType
	 * via bitwise AND, then resolves the fee from its paymentFee config.
	 *
	 * - Flat fee: returns paymentFee.feeAmount directly.
	 * - Percentage fee: returns amountToPay * feeAmount, rounded to 2 decimal places.
	 * - No matching method or missing fee config: returns 0.
	 *
	 * Called by:
	 * - resolveCardFee (card UI fee confirmation screen)
	 * - ReceiptStore.mapTransactionToReceiptData (print/email/text receipts)
	 *
	 * @param item - The cart item containing payment method configurations from the CIS response
	 * @param paymentType - The PaymentType enum value (Cash=1, Card=2, Check=4, etc.)
	 * @returns The calculated fee amount as a number
	 */
	resolvePaymentFee(item, paymentType) {
		try {
			const method = item.item.paymentMethods.find((p) => (p.paymentType & paymentType) !== 0);
			const fee = method?.paymentFee;
			if (!fee || fee.feeAmount === undefined) return 0;
			if (fee.isFeeAmountFlat) return fee.feeAmount;
			return Math.round(item.amountToPay * fee.feeAmount * 100) / 100;
		} catch {
			return 0;
		}
	},

	/**
	 * Returns the invoice number to display on the receipt.
	 * Prefers the cartId (Grant Street transaction ID) if available.
	 * Otherwise returns the item number, optionally obscured for privacy.
	 */
	getReceiptInvoiceNumber() {
		return null;
	},

	/**
	 * Returns the server-generated KioskPaymentId for the receipt's Payment Number row.
	 * Null when absent so the row is omitted entirely.
	 */
	getReceiptPaymentNumber(kioskPaymentId) {
		return kioskPaymentId && kioskPaymentId.length > 0 ? kioskPaymentId : null;
	},

	getPaymentGatewayExternalId(response) {
		// CardConnect returns retref only when it actually authorized the payment
		// (card token, MagTek, CSA ACH). ConEd CheckPostbackOnly is a CIS postback,
		// so its label is a CIS confirmation label and there is no gateway reference.
		return response?.referenceNumberLabel === 'RetRef' ? (response.referenceNumberValue ?? '') : '';
	},

	getCashPaymentGatewayExternalId() {
		// Fiserv never takes cash, so there is no gateway reference to report (#8208).
		return '';
	},

	/**
	 * Returns true for VP5300 messages the Card Payment screen must not display.
	 * Hardcoded for every Fiserv integration; not a config setting.
	 */
	isIgnoredCardDeviceMessage(msg) {
		return IGNORED_DEVICE_MESSAGES.includes(msg.trim().toLowerCase());
	},

	/**
	 * Returns the test panel configuration for the Fiserv integration.
	 * Enables the test panel with an "Fiserv" label.
	 */
	getTestPanelConfig() {
		return { show: true, altText: 'Fiserv' };
	},

	getEMVDeviceState() {
		try {
			return window.KioEMVDevice?.GetState() ?? null;
		} catch {
			return null;
		}
	},

	getEMVDeviceStatus() {
		try {
			const raw = window.KioEMVDevice?.GetStatusEx?.();
			if (raw == null) return null;

			// Level 1: { Status: DeviceStatusFlags, Details: { "EMV Device Status": "<json string>" } | "<json string>" }
			const outer = JSON.parse(raw as unknown as string) as {
				Status: number;
				Details: Record<string, string> | string;
			};

			const detailsBag = typeof outer.Details === 'string' ? (JSON.parse(outer.Details) as Record<string, string>) : outer.Details;

			// Level 2: either a VP5300 envelope { RawData: "<json>" } or the flat device fields themselves
			const innerStr = detailsBag?.['EMV Device Status'];
			const inner = innerStr ? (JSON.parse(innerStr) as Record<string, unknown> & { RawData?: string }) : null;

			let details: StatusDetails = {};
			if (inner?.RawData) {
				details = mapEmvDeviceState(JSON.parse(inner.RawData) as Record<string, unknown>);
			} else if (inner && ('Connected' in inner || 'Started' in inner || 'AppLoaderStatus' in inner)) {
				details = mapEmvDeviceState(inner);
			}

			return {
				statusBits: outer.Status ?? DeviceStatusFlags.Offline,
				details,
			};
		} catch {
			return null;
		}
	},

	runEMVTokenizationTest(onStateChange, onVP5300Msg, onComplete) {
		const device = new EMVDevice('EMV');
		device.setOnStateChanged(onStateChange);
		device.setOnVP5300Msg(onVP5300Msg);
		return device.runTokenization(
			{ PaymentAmount: 100, TransactionRef: crypto.randomUUID() },
			onComplete,
		);
	},

	cancelEMVTest() {
		try {
			window.KioEMVDevice?.CancelTransaction(JSON.stringify({}));
		} catch {
			// ignore
		}
	},
};
